import { Invitation } from "@garther/interfaces";
import { Box, Button } from "@garther/ui/components";
import { Calendar, CheckCircle, Users } from "lucide-react";
import React from "react";
import { useTranslation } from "react-i18next";

interface FriendInvitationSuccessProps {
  invitation: Invitation;
}

export const FriendInvitationSuccess: React.FC<FriendInvitationSuccessProps> = ({ invitation }) => {
  const { t } = useTranslation();

  const formatDate = (date: Date) => {
    return new Intl.DateTimeFormat("en-US", {
      year: "numeric",
      month: "long",
      day: "numeric",
      hour: "2-digit",
      minute: "2-digit"
    }).format(date);
  };

  return (
    <div className="h-full e-center bg-background-primary">
      <div className="max-w-md w-full mx-4">
        <Box>
          <div className="text-center mb-8">
            <div className="w-16 h-16 bg-green-100 rounded-full e-center mx-auto mb-4">
              <CheckCircle className="w-8 h-8 text-green-600" />
            </div>
            <h1 className="heading-2 text-text-primary mb-4">{t("friendInvitation.confirmedTitle")}</h1>
            <p className="body-base text-text-secondary">{t("friendInvitation.confirmedDescription")}</p>
          </div>

          <div className="space-y-6">
            {/* Confirmation Details */}
            <div className="bg-green-50 border border-green-200 rounded-sm p-4">
              <div className="flex items-center space-x-2 mb-2">
                <CheckCircle className="w-5 h-5 text-green-600" />
                <span className="text-green-800 font-medium">
                  {t("friendInvitation.attendanceConfirmed")}
                </span>
              </div>
              <p className="text-green-700 text-sm">{t("friendInvitation.confirmationDetails")}</p>
            </div>

            {/* Event Info */}
            <div className="bg-background-highlight rounded-sm p-4 border border-border-light">
              <h3 className="heading-5 text-text-primary mb-3">{t("friendInvitation.eventInfo")}</h3>
              <div className="space-y-2 text-sm">
                <div className="flex items-center space-x-2">
                  <Calendar className="w-4 h-4 text-text-secondary" />
                  <span className="text-text-secondary">{t("friendInvitation.confirmedAt")}:</span>
                  <span className="text-text-primary">
                    {invitation.respondedAt ? formatDate(invitation.respondedAt) : "N/A"}
                  </span>
                </div>
                <div className="flex items-center space-x-2">
                  <Users className="w-4 h-4 text-text-secondary" />
                  <span className="text-text-secondary">{t("friendInvitation.invitationCode")}:</span>
                  <span className="text-text-primary font-mono">{invitation.id}</span>
                </div>
              </div>
            </div>

            {/* Next Steps */}
            <div className="bg-blue-50 border border-blue-200 rounded-sm p-4">
              <h4 className="text-blue-800 font-medium mb-2">{t("friendInvitation.nextSteps")}</h4>
              <ul className="text-blue-700 text-sm space-y-1">
                <li>• {t("friendInvitation.nextStep1")}</li>
                <li>• {t("friendInvitation.nextStep2")}</li>
                <li>• {t("friendInvitation.nextStep3")}</li>
              </ul>
            </div>

            {/* Action Button */}
            <div className="space-y-3">
              <Button variant="default" onClick={() => (window.location.href = "/")} className="w-full">
                {t("friendInvitation.continueToApp")}
              </Button>

              <Button variant="outline" onClick={() => window.close()} className="w-full">
                {t("common.close")}
              </Button>
            </div>
          </div>
        </Box>
      </div>
    </div>
  );
};
